import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Building2, Users, CreditCard, TrendingUp, 
  Clock, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';

const PlataformaDashboard = () => {
  const { api } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard/plataforma');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Empresas',
      value: dashboard?.total_empresas || 0,
      icon: Building2,
      color: 'bg-blue-500',
    },
    {
      title: 'Empresas Ativas',
      value: dashboard?.empresas_ativas || 0,
      icon: CheckCircle,
      color: 'bg-accent',
    },
    {
      title: 'Pendentes',
      value: dashboard?.empresas_pendentes || 0,
      icon: Clock,
      color: 'bg-warning',
    },
    {
      title: 'Em Teste',
      value: dashboard?.empresas_teste || 0,
      icon: AlertCircle,
      color: 'bg-info',
    },
    {
      title: 'Total Usuários',
      value: dashboard?.total_usuarios || 0,
      icon: Users,
      color: 'bg-purple-500',
    },
    {
      title: 'Total Empregados',
      value: dashboard?.total_empregados || 0,
      icon: Users,
      color: 'bg-pink-500',
    },
    {
      title: 'Receita Mensal',
      value: `R$ ${(dashboard?.receita_mensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: CreditCard,
      color: 'bg-accent',
    },
    {
      title: 'Crescimento',
      value: '+12%',
      icon: TrendingUp,
      color: 'bg-teal-500',
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="plataforma-dashboard">
      <div>
        <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Dashboard</h2>
        <p className="text-muted-foreground">Visão geral da plataforma Jornyx</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="border-slate-100 hover:shadow-md transition-shadow" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-primary">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit']">Empresas Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma empresa cadastrada ainda</p>
              <Button className="mt-4 bg-accent hover:bg-accent/90">
                Cadastrar Empresa
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit']">Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma atividade registrada</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlataformaDashboard;
